/**
 * RegionService — on-demand region materialization with an LRU graph cache and
 * neighbour residency (W2b-1 Task 4).
 *
 * A planet has far more region cells than any frame touches, and each cell's
 * CityGraph is expensive to grow (arterials → space-colonization → densify →
 * parcels). This service is the lazy front door: {@link region} returns a cell's
 * graph, materializing it exactly once and caching it, and every materialization
 * is folded into a single planet-wide {@link SettlementIndex} so the terrain hot
 * path (Task 3's carve stage) sees every settlement the service has ever built.
 *
 * MEMORY SHAPE (documented, deliberate)
 * -------------------------------------
 * The LRU evicts GRAPHS ONLY — the heavy node/edge/parcel arrays. It NEVER drops
 * index features: `addRegion` folds a region's segments/quads into the index the
 * first time it is materialized, and they stay for the service's lifetime. So the
 * index accumulates ∝ settlements TOUCHED, not settlements resident. That is the
 * right trade for W2a-scale planets (features are tiny — a handful of floats each
 * — and the carve hot path needs them regardless of graph residency); W2b-2
 * revisits if profiling on larger worlds says otherwise. The consequence that
 * matters for correctness: carve results are STABLE under graph eviction, because
 * the features a query reads never leave the index.
 *
 * DETERMINISM = CACHE TRANSPARENCY
 * --------------------------------
 * `materializeRegion` is a pure function of (sampler, cell), so re-materializing
 * an evicted region yields a BYTE-IDENTICAL graph, and `addRegion` is idempotent
 * per region cell (keyed on packCell), so re-folding it is a no-op. The cache is
 * therefore transparent: a warmed service and a fresh service answer identically,
 * and eviction can never shift a byte. The Task-4 oracles pin exactly this.
 *
 * NEIGHBOUR RESIDENCY
 * -------------------
 * Growth OVERGROWS its cell (arterials run up to OVERGROW_M past the nominal
 * bounds; streets/parcels straddle the edge), so a carve probe near a region
 * border must consult the neighbour graphs too (spec §5.1). {@link ensureAround}
 * materializes the full 3×3 neighbourhood of a cell — the cell, its 4 edge
 * neighbours, and its 4 corner neighbours — folding each into the index so the
 * border is correct from either side. Because folding is permanent (see the
 * memory shape above), the residency guarantee is about the INDEX, not graph
 * residency: once ensureAround has run for a cell, that cell's border features
 * are in the index even if the LRU later evicts the graphs.
 */
import type { PlanetSampler } from "../universe.js";
import type { CityGraph } from "./citygraph.js";
import { type CellId, packCell } from "./cells.js";
import { SettlementIndex } from "./carveindex.js";
import { materializeRegion } from "./growth.js";
import { edgeNeighbor, SIDE_NEG_U, SIDE_POS_U, SIDE_NEG_V, SIDE_POS_V } from "./boundary.js";

/** Default resident-graph budget — comfortably above the 9-cell 3×3 working set. */
export const DEFAULT_LRU = 64;

/**
 * Lazy region-graph provider over one planet. Construct with the planet's
 * sampler; call {@link region} to get a cell's graph (materialized once, cached)
 * or {@link ensureAround} to make a cell's whole border neighbourhood resident.
 * All materializations feed the shared {@link index}.
 *
 * NOTE: RegionService does NOT auto-attach its index to the sampler's carve stage;
 * a caller that wants carve-integrated terrain samples must wire it explicitly via
 * `sampler.setSettlementIndex(service.index)`.
 */
export class RegionService {
  private readonly sampler: PlanetSampler;
  private readonly radius: number;
  private readonly lru: number;
  /** Resident graphs, packCell(cell) → graph. Map insertion order == LRU order
   *  (least-recently-used first); a touch deletes then re-sets to move to back. */
  private readonly graphs = new Map<number, CityGraph>();

  /** The planet-wide carve index every region() call folds its graph into. */
  readonly index: SettlementIndex;

  constructor(sampler: PlanetSampler, opts?: { lru?: number }) {
    this.sampler = sampler;
    this.radius = sampler.def.radius;
    // A cap below 1 would evict every graph the instant it lands (index still
    // accumulates), so floor at 1 — the service always keeps at least the graph
    // it just returned resident.
    this.lru = Math.max(1, opts?.lru ?? DEFAULT_LRU);
    this.index = new SettlementIndex(sampler);
  }

  /**
   * The cell's CityGraph: the cached instance if resident, else materialized now
   * (and folded into {@link index}, idempotently). Synchronous — W2b-2 wraps this
   * in the region worker. A cache HIT moves the graph to the back of the LRU; a
   * MISS materializes, folds, caches, and evicts the least-recently-used graph(s)
   * back down to the budget. Re-materialization after eviction is byte-identical.
   */
  region(cell: CellId): CityGraph {
    const key = packCell(cell);
    const cached = this.graphs.get(key);
    if (cached !== undefined) {
      // Touch: re-insert at the back so it is the most-recently-used.
      this.graphs.delete(key);
      this.graphs.set(key, cached);
      return cached;
    }
    const g = materializeRegion(this.sampler, cell);
    // Fold once into the index (idempotent per region cell, so re-materializing
    // an evicted region and re-adding it here is a safe no-op on the index).
    this.index.addRegion(g);
    this.graphs.set(key, g);
    // Evict LRU-first until within budget. The graph just inserted is at the
    // back, so it is never the one evicted (budget is floored at 1).
    while (this.graphs.size > this.lru) {
      const oldest = this.graphs.keys().next().value as number;
      this.graphs.delete(oldest);
    }
    return g;
  }

  /**
   * The cell's graph IF it is already resident in the LRU — otherwise null. NEVER
   * materializes and never touches the LRU order: a pure read, safe to call from a
   * per-frame path where the multi-hundred-ms grow that {@link region} would do on a
   * miss is not an option. A caller that wants a missing cell should ask for it
   * asynchronously (the client's worker/cache batch) and peek again next frame.
   */
  peek(cell: CellId): CityGraph | null {
    return this.graphs.get(packCell(cell)) ?? null;
  }

  /**
   * Make the 3×3 neighbourhood around `cell` resident: the cell, its 4 edge
   * neighbours, and its 4 corner neighbours — each materialized via
   * {@link region} and thus folded into {@link index}. A corner is the neighbour
   * of an EDGE neighbour on a PERPENDICULAR side (e.g. the −U neighbour's −V
   * neighbour), which is how cube-face adjacency reaches diagonals without a
   * topology table. Cells are deduped by packCell — near a cube corner or on a
   * tiny (1×1) planet several of the nine collapse onto the same cell, and each
   * is visited at most once.
   *
   * The point is index residency (border carve correctness), which persists even
   * if the LRU later evicts the graphs; see the module header's memory-shape note.
   *
   * Returns the cells this call NEWLY materialized — those whose region was not yet
   * folded into {@link index} before the call (deduped, at most nine). A cell already
   * resident (from a prior call, even if its graph was since LRU-evicted) is NOT
   * returned. The W2b-2 client residency loop uses this to know exactly which region
   * graphs to broadcast to the terrain workers; a second call for the same cell (all
   * resident) returns `[]`.
   */
  ensureAround(cell: CellId): CellId[] {
    const seen = new Set<number>();
    const fresh: CellId[] = [];
    const visit = (c: CellId): void => {
      const k = packCell(c);
      if (seen.has(k)) return;
      seen.add(k);
      // "Newly materialized" == not yet in the index (folding is permanent, so this
      // is stable under graph eviction). Check BEFORE region() folds it in.
      const wasResident = this.index.has(k);
      this.region(c);
      if (!wasResident) fresh.push(c);
    };

    visit(cell);

    const R = this.radius;
    const negU = edgeNeighbor(R, cell, SIDE_NEG_U);
    const posU = edgeNeighbor(R, cell, SIDE_POS_U);
    const negV = edgeNeighbor(R, cell, SIDE_NEG_V);
    const posV = edgeNeighbor(R, cell, SIDE_POS_V);

    // Corners: step off a U-side neighbour along the perpendicular (V) axis.
    const cornerNN = edgeNeighbor(R, negU, SIDE_NEG_V);
    const cornerNP = edgeNeighbor(R, negU, SIDE_POS_V);
    const cornerPN = edgeNeighbor(R, posU, SIDE_NEG_V);
    const cornerPP = edgeNeighbor(R, posU, SIDE_POS_V);

    visit(negU);
    visit(posU);
    visit(negV);
    visit(posV);
    visit(cornerNN);
    visit(cornerNP);
    visit(cornerPN);
    visit(cornerPP);

    return fresh;
  }

  /**
   * Adopt a cache-fed graph WITHOUT re-materializing it (W2b-2 Task 3). The
   * IndexedDB region cache deserializes a stored CityGraph buffer and hands it
   * here; adopt folds it into {@link index} and makes it resident exactly as
   * {@link region} would for a freshly-grown graph — same idempotent index fold,
   * same LRU participation — but skips the (expensive) growth pipeline entirely.
   *
   * CORRECTNESS = CACHE TRANSPARENCY (same argument as region())
   * ------------------------------------------------------------
   * A cached buffer is the serialization of some earlier `materializeRegion(sampler,
   * cell)`, and materialization is a pure function of (sampler, cell) with a
   * byte-stable serialization (see citygraph.ts). So the graph adopt() receives is
   * byte-identical to what region() would grow for the same cell on THIS planet, and
   * `index.addRegion` is idempotent per region cell — hence adopting then querying is
   * indistinguishable from materializing then querying (the Task-3 oracle pins this).
   * The version tag baked into the cache key guarantees the buffer was produced by the
   * same CITYGRAPH_VERSION, so a format/content bump can never feed a stale graph here.
   *
   * IDEMPOTENT: if the cell is already resident (graph in the LRU, or folded into the
   * index by a prior region()/adopt() even if since evicted) this only touches the LRU
   * — it never double-folds (addRegion guards on packCell) and never regresses a graph.
   */
  adopt(g: CityGraph): void {
    const key = packCell(g.regionId);
    const cached = this.graphs.get(key);
    if (cached !== undefined) {
      // Already resident: touch to MRU, keep the incumbent instance (no-op fold).
      this.graphs.delete(key);
      this.graphs.set(key, cached);
      return;
    }
    // Fold once into the index (idempotent per region cell — safe even if the graph
    // was resident-then-evicted and only its features remain in the index).
    this.index.addRegion(g);
    this.graphs.set(key, g);
    // Evict LRU-first until within budget (identical policy to region()).
    while (this.graphs.size > this.lru) {
      const oldest = this.graphs.keys().next().value as number;
      this.graphs.delete(oldest);
    }
  }

  /** Number of graphs currently resident (≤ the LRU budget). For tests/telemetry. */
  get residentCount(): number {
    return this.graphs.size;
  }
}
